// ---------------------------------------------------------------------------
// readingThemeService — ponto ÚNICO de gravação de `users.currentReadingTheme`.
//
// Espelha `profileMusicService.ts`: o campo é client-locked (whitelist do
// update + blocklist do create nas Rules), então nenhum caminho do client
// escreve via `updateUserProfile`. Blueprint: docs/systems/reading-theme.md.
// ---------------------------------------------------------------------------

import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";

const functionsRegion = import.meta.env.VITE_FIREBASE_FUNCTIONS_REGION as string | undefined;

function getFn() {
  const app = getApp();
  return functionsRegion?.trim() ? getFunctions(app, functionsRegion.trim()) : getFunctions(app);
}

export interface GenerateReadingThemeResult {
  status: "ok";
  floor: number;
}

/**
 * Grava o tema já derivado e aprovado no gate CLIENT-SIDE
 * (`resolveReadingTheme`, `src/config/readingTheme.ts`) — o servidor confere
 * a PROPRIEDADE (contraste real) e a AUTORIZAÇÃO (isPro agora), não refaz a
 * extração de pixel. Chamar isto sem antes checar `status === "ok"` do lado
 * do client é desperdiçar uma chamada que o servidor vai recusar.
 */
export interface GenerateReadingThemeInput {
  userBookId: string;
  tokens: Record<string, string>;
  /** v3 (27/09): papel claro ou escuro — o que o seletor/auto decidiu. */
  mode: "light" | "dark";
  engineVersion: number;
  /**
   * A capa RESOLVIDA que gerou — a chave de "já gerado" (BUG-297). Só vai
   * quando é `https://`: o schema recusa o resto, e uma capa `http://` legada
   * não pode derrubar a gravação do tema inteiro por causa de uma chave.
   */
  coverUrl?: string;
}

/**
 * Os três campos da v3 são opcionais no schema da CF (`.strict()`): o build
 * 1.10 embarcado continua mandando só `{userBookId, tokens}`. Este client
 * manda sempre — por isso as functions sobem ANTES do hosting.
 */
export async function generateReadingTheme(input: GenerateReadingThemeInput): Promise<GenerateReadingThemeResult> {
  const fn = httpsCallable<GenerateReadingThemeInput, GenerateReadingThemeResult>(getFn(), "generateReadingTheme");
  const res = await fn(input);
  if (res.data?.status !== "ok") throw new Error("Resposta inválida do servidor.");
  return res.data;
}

export async function clearReadingTheme(): Promise<void> {
  const fn = httpsCallable<Record<string, never>, { status: "ok" }>(getFn(), "clearReadingTheme");
  await fn({});
}

/**
 * Códigos em que o SDK não traz mensagem de servidor — mesma lista de
 * `profileMusicService.ts`/`creatorRedeemMessage.ts`.
 */
const INFRA_ERROR_CODES = new Set([
  "functions/internal",
  "functions/unavailable",
  "functions/deadline-exceeded",
  "functions/cancelled",
  "functions/aborted",
  "functions/unknown",
]);

const GENERIC_ERROR = "Não foi possível gerar o tema agora. Tente de novo em instantes.";

/**
 * Erro de regra de negócio (`permission-denied`, `failed-precondition`,
 * `invalid-argument`) já vem do servidor como frase pronta em português e é
 * repassado intacto. Falha de infra vira frase genérica.
 */
export function readingThemeErrorMessage(err: unknown): string {
  const code = (err as { code?: string } | null)?.code ?? "";
  if (INFRA_ERROR_CODES.has(code)) return GENERIC_ERROR;
  const message = err instanceof Error ? err.message.trim() : "";
  if (!message || !message.includes(" ")) return GENERIC_ERROR;
  return message;
}
